export type VideoCallPlatform =
  | 'zoom'
  | 'google_meet'
  | 'microsoft_teams'
  | 'webex'
  | 'slack'
  | 'ringcentral';

const normalizeHost = (host: string) => host.toLowerCase().replace(/\.$/, '');

const hostMatches = (host: string, domain: string) =>
  host === domain || host.endsWith(`.${domain}`);

const parseVideoCallUrl = (value: unknown): URL | null => {
  const normalized = normalizeVideoCallLink(value);
  if (!normalized) return null;

  try {
    const url = new URL(normalized);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    return url;
  } catch {
    return null;
  }
};

export const normalizeVideoCallLink = (value: unknown): string =>
  String(value || '').trim();

export const getVideoCallPlatform = (
  value: unknown,
): VideoCallPlatform | null => {
  const url = parseVideoCallUrl(value);
  if (!url) return null;

  const host = normalizeHost(url.hostname);

  if (hostMatches(host, 'zoom.us')) return 'zoom';
  if (host === 'meet.google.com') return 'google_meet';
  if (
    hostMatches(host, 'teams.microsoft.com') ||
    hostMatches(host, 'teams.live.com')
  ) {
    return 'microsoft_teams';
  }
  if (hostMatches(host, 'webex.com')) return 'webex';
  if (hostMatches(host, 'slack.com')) return 'slack';
  if (hostMatches(host, 'ringcentral.com')) return 'ringcentral';

  return null;
};

export const isSupportedVideoCallLink = (value: unknown): boolean =>
  Boolean(getVideoCallPlatform(value));

export const isRingCentralVideoCallLink = (value: unknown): boolean =>
  getVideoCallPlatform(value) === 'ringcentral';

export const canScheduleVideoCallLink = (value: unknown): boolean => {
  const platform = getVideoCallPlatform(value);
  return Boolean(platform && platform !== 'ringcentral');
};
